import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Emblem } from "./Primitives";

const LINKS = [
  { label: "Videos", id: "videos" },
  { label: "Topics", id: "topics" },
  { label: "About", id: "about" },
  { label: "Free Guide", to: "/free-guide" },
  { label: "Subscribe", id: "newsletter" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.to) {
      navigate(link.to);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${link.id}`);
      return;
    }
    document.getElementById(link.id)?.scrollIntoView({ behavior: "smooth" });
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-black/40 border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 h-[72px] flex items-center justify-between">
          <button
            data-testid="logo-home"
            onClick={goHome}
            className="flex items-center gap-3 group"
          >
            <Emblem size={26} className="text-white transition-transform duration-500 group-hover:rotate-90" />
            <span className="font-display text-xl md:text-2xl tracking-[0.15em] leading-none">
              NOIR <span className="text-crimson">PSYCHE</span>
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-10">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-${(l.id || l.label.toLowerCase().replace(/\s+/g, "-"))}`}
                onClick={() => go(l)}
                className="link-underline font-mono-accent text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="md:hidden text-white"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 md:hidden bg-[#050505] flex flex-col items-center justify-center gap-8"
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.label}
                data-testid={`mobile-nav-${(l.id || l.label.toLowerCase().replace(/\s+/g, "-"))}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
                onClick={() => go(l)}
                className="font-display text-4xl tracking-wide text-white/80 hover:text-crimson transition-colors"
              >
                {l.label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
